import React, { useEffect, useState, useMemo } from "react";
import { Box, useTheme, useMediaQuery } from "@mui/material";
import { Grid } from "@mui/material";
import { FormProvider } from "react-hook-form";
import PageHeader from "@ui/components/header/PageHeader";
import { PageContainer, PageContent } from "@ui/components/wraper";
import { FInput } from "@ui/components/inputs/FInput";
import { FSelect } from "@ui/components/inputs/FSelect";
import { SAutoComplete } from "@ui/components/inputs/SAutoComplete";
import { InfoBanner } from "@ui/components/info";
import { useTranslation } from "react-i18next";
import { useUserData } from "../hooks/useUserData";
import { useUserForm } from "../hooks/useUserForm";
import { useDispatch } from "react-redux";
import { useAppSelector } from "@application/store/useStore";
import { getRolesDropdown } from "@reducers/roles/thunk/role-thunk";
import { getAccessPointsDropdown } from "@reducers/access-points/thunk/access-point-thunk";
import "@reducers/injectReducters";
import { userEditStorage } from "@utility/user-edit-storage";
import { useNavigate } from "react-router-dom";
const AddEditUserPage = () => {
    var _a, _b, _c, _d;
    const { t } = useTranslation(["form", "validation", "common"]);
    const [showInfoBanner, setShowInfoBanner] = useState(true);
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { selectedUserRow } = useAppSelector((state) => state.user);
    const { rolesDropdown, dropdownLoading } = useAppSelector((state) => state.role);
    const { accessPointsDropdown, dropdownLoading: apDropdownLoading } = useAppSelector((state) => state.accessPoint);
    useEffect(() => {
        // Only fetch if not already loaded and not currently loading
        if (!rolesDropdown && !dropdownLoading) {
            dispatch(getRolesDropdown());
        }
        dispatch(getAccessPointsDropdown());
    }, [dispatch, rolesDropdown, dropdownLoading]);
    // Clear local storage when component unmounts (user navigates away)
    useEffect(() => {
        return () => {
            // Only clear if navigating back to users list or other pages
            // The useUserForm hook handles clearing after successful update
            const currentPath = window.location.pathname;
            if (!currentPath.includes("/users/")) {
                userEditStorage.clearUserEditData();
            }
        };
    }, []);
    const roleOptions = React.useMemo(() => {
        if (!rolesDropdown)
            return [{ value: "", label: t("common:actions.selectRole") }];
        return [
            { value: "", label: t("common:actions.selectRole") },
            ...rolesDropdown.map((role) => ({ value: role.id, label: role.name })),
        ];
    }, [rolesDropdown]);
    // Convert access points to dropdown options for SAutoComplete
    const accessPointOptions = useMemo(() => {
        if (!accessPointsDropdown)
            return [];
        return accessPointsDropdown.map((ap) => {
            var _a;
            return ({
                label: ((_a = ap.name) === null || _a === void 0 ? void 0 : _a.trim()) || ap.id,
                value: ap.id,
            });
        });
    }, [accessPointsDropdown]);
    const { isEdit, user, userExists } = useUserData();
    const { methods, control, errors, handleSubmit, validationRules, handleFormSubmit, isLoading, } = useUserForm({ isEdit, selectedUser: selectedUserRow });
    const userInfoBulletPoints = [
        t("common:userInfo.emailOnCreate"),
        t("common:userInfo.adminAccess"),
        t("common:userInfo.copySettings"),
    ];
    // Only show "User Not Found" if we're in edit mode, have no selected user,
    // and also have no data in local storage (page reload scenario)
    // The useUserForm hook will handle loading from local storage if available
    // if (selectedUserRow == null && isEdit) {
    //   return (
    //     <PageContainer>
    //       <PageHeader title="User Not Found" showBackButton />
    //     </PageContainer>
    //   );
    // }
    // Action button configuration
    const actionButton = {
        label: isEdit
            ? t("common:actions.updateUser")
            : t("common:actions.addUser"),
        onClick: handleSubmit(handleFormSubmit),
        variant: "contained",
        size: "large",
        disabled: isLoading,
        loading: isLoading,
    };
    return (React.createElement(PageContainer, null,
        React.createElement(PageHeader, { title: isEdit ? t("common:pages.editUser") : t("common:pages.addUser"), showBackButton: true, bottomActionButton: actionButton }),
        React.createElement(PageContent, { hasBottomAction: true },
            showInfoBanner && (React.createElement(Box, { sx: { mb: 3 } },
                React.createElement(InfoBanner, { title: t("common:actions.important"), variant: "user", bulletPoints: userInfoBulletPoints, onClose: () => setShowInfoBanner(false) }))),
            React.createElement(FormProvider, Object.assign({}, methods),
                React.createElement("form", { onSubmit: handleSubmit(handleFormSubmit) },
                    React.createElement(Grid, { container: true, spacing: 1 },
                        React.createElement(Grid, { size: { xs: 12 } },
                            React.createElement(FSelect, { name: "selectedRole", control: control, label: t("common:labels.role"), options: roleOptions, error: errors.selectedRole, helperText: (_a = errors.selectedRole) === null || _a === void 0 ? void 0 : _a.message, rules: validationRules.selectedRole })),
                        React.createElement(Grid, { size: { xs: 12 } },
                            React.createElement(FInput, { name: "firstName", control: control, label: t("common:labels.firstName"), error: errors.firstName, helperText: (_b = errors.firstName) === null || _b === void 0 ? void 0 : _b.message, rules: validationRules.firstName })),
                        React.createElement(Grid, { size: { xs: 12 } },
                            React.createElement(FInput, { name: "lastName", control: control, label: t("common:labels.lastName"), error: errors.lastName, helperText: (_c = errors.lastName) === null || _c === void 0 ? void 0 : _c.message, rules: validationRules.lastName })),
                        React.createElement(Grid, { size: { xs: 12 } },
                            React.createElement(FInput, { name: "email", control: control, label: t("common:labels.email"), error: errors.email, helperText: (_d = errors.email) === null || _d === void 0 ? void 0 : _d.message, rules: validationRules.email })),
                        React.createElement(Grid, { size: { xs: 12 } },
                            React.createElement(SAutoComplete, { name: "accessPoints", control: control, label: t("common:labels.accessPoints"), options: accessPointOptions, loading: apDropdownLoading }))))))));
};
export default AddEditUserPage;
